import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CmsService, EventService, withdrawOn } from '@spartacus/core';
import { BehaviorSubject, combineLatest, merge, Observable, of, Subscription, zip } from 'rxjs';
import {
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  AreaOfUseService,
  ArticleService,
  CatalogKeysService,
  CategoryService,
  DocumentsService,
  ProductService,
} from '../../../../core/catalog';
import {
  AREA_OF_USE_ALL_CATEGORIES_CODE,
  Article,
  ArticleCarouselActionMode,
  ArticleCarouselItem,
  Category,
  CmsTabContainerComponent,
  PdpTabTypes,
  Product,
  ProductDetailsTopic,
  ProductRelationTargetType,
} from '../../../../core/model';
import {
  ProductCertificateGroupCustomizationEnum,
  ProductCertification,
  ProductRelation,
} from '../../../../core/occ/occ-models/occ-generated.models';
import { LanguageSetEvent } from '../../../../core/site-context';
import { PrincipalConfigurationService } from '../../../../core/user';
import { prepareUrlForLink, shallowEqualObjects } from '../../../../core/util';
import { EnterPDPEvent, ViewRelatedItemsEvent } from '../../../../events/misc';
import { WindowUtils } from '../../../../shared';
import { ProductCarouselItem } from '../../../../shared/components/product-carousel';
import { ProductDetailsService } from '../../base/services';
import { CatalogSearchService } from '../catalog/catalog-search.service';

@Component({
  selector: 'py-pdp',
  templateUrl: './pdp.component.html',
  styleUrls: ['./pdp.component.scss'],
  providers: [ProductDetailsService],
  host: {
    ngSkipHydration: 'true',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PdpComponent implements OnInit, OnDestroy {
  @ViewChild('productInformation') productInformation?: ElementRef<HTMLDivElement>;
  @ViewChild('pdpOverview') pdpOverview?: ElementRef<HTMLDivElement>;

  readonly carouselActionModes: typeof ArticleCarouselActionMode = ArticleCarouselActionMode;
  product$: Observable<Product>;
  article$: Observable<Article | undefined>;
  areaOfUseCode$: Observable<string>;
  replacementArticle$: Observable<Article | undefined>;
  category$: Observable<Category>;
  activeTabIndex$ = new BehaviorSubject<PdpTabTypes | string | undefined>(undefined);
  relatedProducts$: Observable<ProductCarouselItem[]>;
  relatedArticles$: Observable<ArticleCarouselItem[]>;
  hasRelatedProducts$: Observable<boolean>;
  pdpTypeSkuPicker$: Observable<boolean>;
  enableSecondaryArticleRowVariant$: Observable<boolean>;
  catalogProductItemSecondary$: Observable<boolean>;
  tabContainers$: Observable<CmsTabContainerComponent[]>;

  showAListOfAllAvailableVariantsLink = true;
  outOfStock: boolean;

  private subscription = new Subscription();
  private productCode$: Observable<string>;
  private articleCode$: Observable<string>;

  constructor(
    private productService: ProductService,
    private articleService: ArticleService,
    private categoryService: CategoryService,
    private activatedRoute: ActivatedRoute,
    protected router: Router,
    private catalogKeysService: CatalogKeysService,
    private cd: ChangeDetectorRef,
    private windowUtils: WindowUtils,
    private documentService: DocumentsService,
    private areaOfUseService: AreaOfUseService,
    private eventService: EventService,
    private catalogSearchService: CatalogSearchService,
    private principalConfigurationService: PrincipalConfigurationService,
    private cmsService: CmsService,
    private productDetailsService: ProductDetailsService
  ) {}

  ngOnInit(): void {
    this.areaOfUseCode$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('aou')),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const productCode$ = this.activatedRoute.data.pipe(
      map((data) => data?.productCode),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.productCode$ = productCode$;

    this.product$ = productCode$.pipe(
      switchMap((code) => this.productService.getProduct(code)),
      filter((product) => !!product),
      tap((product) => this.productDetailsService.setProduct(product)),
      distinctUntilChanged(shallowEqualObjects),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const articleCode$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('a')),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.articleCode$ = articleCode$;

    this.article$ = combineLatest([articleCode$, this.product$]).pipe(
      switchMap(([code, product]) => {
        if (product?.articles?.includes(code)) {
          return this.articleService.getArticle(code);
        }
        return of(undefined);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.replacementArticle$ = this.article$.pipe(
      switchMap((article) => {
        if (!!article && article.replaces?.length > 0) {
          return this.articleService.getArticle(article.replaces[0].target);
        }
        return of(undefined);
      })
    );

    this.category$ = combineLatest([this.activatedRoute.data, this.activatedRoute.queryParamMap]).pipe(
      distinctUntilChanged(
        (prev, curr) =>
          shallowEqualObjects(prev[0], curr[0]) &&
          prev[1].get('aou') === curr[1].get('aou') &&
          prev[1].get('mya') === curr[1].get('mya')
      ),
      switchMap(([data, queryParams]) => {
        if (queryParams.get('aou') && queryParams.get('aou') !== AREA_OF_USE_ALL_CATEGORIES_CODE) {
          return this.areaOfUseService.getAreaOfUse(queryParams.get('aou'));
        } else if (data?.categoryCode) {
          return this.catalogKeysService
            .getCatalogUsageKeyForParams(queryParams.get('mya'))
            .pipe(switchMap((catalogUsageKey) => this.categoryService.getCategory(data?.categoryCode, catalogUsageKey)));
        }
        return of(undefined);
      }),
      tap((_) => this.cd.detectChanges()),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.enableSecondaryArticleRowVariant$ = this.principalConfigurationService
      .isEnabled('enableSecondaryArticleRowVariant')
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.catalogProductItemSecondary$ = this.principalConfigurationService
      .isEnabled('catalogProductItemSecondary')
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.tabContainers$ = this.product$.pipe(
      switchMap((product) =>
        combineLatest([
          ...(product.tabContainerIds ?? []).map((tabContainerId) =>
            this.cmsService.getComponentData<CmsTabContainerComponent>(tabContainerId).pipe(
              map((tabContainer: CmsTabContainerComponent) =>
                tabContainer?.components?.length > 0
                  ? {
                      ...tabContainer,
                      flexType: 'TabContainerComponent',
                    }
                  : null
              )
            )
          ),
        ]).pipe(
          map((tabContainers) => tabContainers.filter((tabContainer) => tabContainer !== null)),
          defaultIfEmpty([])
        )
      ),
      startWith([]),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.initRelatedProductsObservables();

    const productCerts$ = this.product$.pipe(
      switchMap(() => {
        return this.productDetailsService.getCertLabels();
      }),
      filter((cert) => !!cert)
    );

    const articleCerts$ = this.article$.pipe(map((article) => article?.certLabels || {}));

    this.subscription.add(
      combineLatest([
        this.product$.pipe(distinctUntilChanged(shallowEqualObjects)),
        this.article$.pipe(distinctUntilChanged(shallowEqualObjects)),
        this.tabContainers$,
        productCerts$,
        articleCerts$,
      ])
        .pipe(
          filter(([product, article]) => !!product || !!article),
          switchMap(([product, article, tabContainers, productCerts, articleCerts]) =>
            this.documentService.getProductDocuments(product?.code, article?.code).pipe(
              map((productDocuments) => ({
                product,
                article,
                tabContainers,
                productDocuments,
                productCerts,
                articleCerts,
              })),
              withLatestFrom(this.catalogSearchService.isPdpVersionSkuPicker())
            )
          )
        )
        .subscribe(
          ([{ product, article, productDocuments, tabContainers, productCerts, articleCerts }, isPdpVersionSkuPicker]) => {
            if (isPdpVersionSkuPicker) {
              if (
                product?.articles?.length > 1 &&
                (!product.articlePickerAttributes || product.articlePickerAttributes?.length === 0)
              ) {
                this.activeTabIndex$.next(PdpTabTypes.ArticleList);
              } else if (
                product.longDescription ||
                product.uniqueSellingPoints?.points?.length ||
                this.hasCertCustomization(productCerts, ProductCertificateGroupCustomizationEnum.WASHING_INSTRUCTIONS) ||
                this.hasCertCustomization(articleCerts, ProductCertificateGroupCustomizationEnum.WASHING_INSTRUCTIONS) ||
                this.hasCertCustomization(productCerts, ProductCertificateGroupCustomizationEnum.MATERIAL_COMPOSITION) ||
                this.hasCertCustomization(articleCerts, ProductCertificateGroupCustomizationEnum.MATERIAL_COMPOSITION)
              ) {
                this.activeTabIndex$.next(PdpTabTypes.ProductInformation);
              } else if (
                product.productDownloadables ||
                !!product.datasheet?.url ||
                !!article?.safetyDatasheetUrl ||
                !!article?.datasheet?.url ||
                productDocuments?.length > 0
              ) {
                this.activeTabIndex$.next(PdpTabTypes.Documents);
              } else if (product.extraInfo?.topics?.includes(ProductDetailsTopic.TECHNICAL_DATA_TABLE)) {
                this.activeTabIndex$.next(PdpTabTypes.TechnicalAttributes);
              } else if (product.extraInfo?.topics?.includes(ProductDetailsTopic.ASSORTMENT_PACKAGING_TABLE)) {
                this.activeTabIndex$.next(PdpTabTypes.Assortment);
              } else if (
                this.hasCertCustomization(productCerts, ProductCertificateGroupCustomizationEnum.DEFAULT) ||
                this.hasCertCustomization(articleCerts, ProductCertificateGroupCustomizationEnum.DEFAULT)
              ) {
                this.activeTabIndex$.next(PdpTabTypes.CertificateInformation);
              } else if (
                product.extraInfo?.technicalCapabilities &&
                Object.keys(product.extraInfo?.technicalCapabilities)?.length > 0
              ) {
                this.activeTabIndex$.next(PdpTabTypes.TechnicalCapabilities);
              } else if (
                (this.hasCertCustomization(articleCerts, ProductCertificateGroupCustomizationEnum.PRINTING_TECHNOLOGY),
                this.hasCertCustomization(productCerts, ProductCertificateGroupCustomizationEnum.PRINTING_TECHNOLOGY))
              ) {
                this.activeTabIndex$.next(PdpTabTypes.PrintingTechnologies);
              } else if (product.extraInfo?.applications?.length > 0) {
                this.activeTabIndex$.next(PdpTabTypes.Applications);
              } else if (tabContainers?.length) {
                this.activeTabIndex$.next(tabContainers[0].uid);
              } else {
                this.activeTabIndex$.next(undefined);
              }
            } else {
              if (!!product?.articles?.length) {
                this.activeTabIndex$.next(PdpTabTypes.ArticleList);
              } else if (product.extraInfo?.topics?.includes(ProductDetailsTopic.TECHNICAL_DATA_TABLE)) {
                this.activeTabIndex$.next(PdpTabTypes.TechnicalAttributes);
              } else if (product.extraInfo?.topics?.includes(ProductDetailsTopic.ASSORTMENT_PACKAGING_TABLE)) {
                this.activeTabIndex$.next(PdpTabTypes.Assortment);
              } else if (
                this.hasCertCustomization(productCerts, ProductCertificateGroupCustomizationEnum.DEFAULT) ||
                this.hasCertCustomization(articleCerts, ProductCertificateGroupCustomizationEnum.DEFAULT)
              ) {
                this.activeTabIndex$.next(PdpTabTypes.CertificateInformation);
              } else if (
                product.extraInfo?.technicalCapabilities &&
                Object.keys(product.extraInfo?.technicalCapabilities)?.length > 0
              ) {
                this.activeTabIndex$.next(PdpTabTypes.TechnicalCapabilities);
              } else if (
                this.hasCertCustomization(articleCerts, ProductCertificateGroupCustomizationEnum.PRINTING_TECHNOLOGY) ||
                this.hasCertCustomization(productCerts, ProductCertificateGroupCustomizationEnum.PRINTING_TECHNOLOGY)
              ) {
                this.activeTabIndex$.next(PdpTabTypes.PrintingTechnologies);
              } else if (product.extraInfo?.applications?.length > 0) {
                this.activeTabIndex$.next(PdpTabTypes.Applications);
              } else if (
                product.longDescription ||
                product.uniqueSellingPoints?.points?.length ||
                product.productDownloadables ||
                !!product.datasheet?.url ||
                !!article?.safetyDatasheetUrl ||
                !!article?.datasheet?.url ||
                productDocuments?.length > 0 ||
                this.hasCertCustomization(articleCerts, ProductCertificateGroupCustomizationEnum.WASHING_INSTRUCTIONS) ||
                this.hasCertCustomization(productCerts, ProductCertificateGroupCustomizationEnum.WASHING_INSTRUCTIONS)
              ) {
                this.activeTabIndex$.next(PdpTabTypes.ProductInformation);
              } else if (tabContainers?.length) {
                this.activeTabIndex$.next(tabContainers[0].uid);
              } else {
                this.activeTabIndex$.next(undefined);
              }
            }
          }
        )
    );

    this.subscription.add(
      this.eventService
        .get(LanguageSetEvent)
        .pipe(
          switchMap(() =>
            this.product$.pipe(
              take(1),
              filter((product) => !!product?.code)
            )
          )
        )
        .subscribe((product) => this.documentService.loadProductDocuments(product.code, true))
    );

    this.subscription.add(
      this.product$
        .pipe(
          filter((product) => !!product?.code),
          distinctUntilChanged((a, b) => a?.code === b?.code)
        )
        .subscribe((product) => {
          const ticket = this.activatedRoute.snapshot?.queryParams?.ticket;
          return this.eventService.dispatch(new EnterPDPEvent({ ...product, ticket }));
        })
    );

    this.pdpTypeSkuPicker$ = this.catalogSearchService.isPdpVersionSkuPicker();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  scrollToProductInformation(tab?: PdpTabTypes): void {
    if (tab) {
      this.activeTabIndex$.next(tab);
    }
    if (this.productInformation) {
      this.windowUtils.scrollIntoView(this.productInformation.nativeElement);
    }
  }

  private initRelatedProductsObservables(): void {
    const contextChanged$ = merge(
      this.articleCode$.pipe(distinctUntilChanged(), skip(1)),
      this.productCode$.pipe(distinctUntilChanged(), skip(1))
    ).pipe(mapTo(undefined));

    /*
     * Merge all possible sources of product relations in the order of priority:
     *   1. relations of selected/active article (only used when viewing an 'article')
     *   2. relations of current product
     *   3. relations of articles belonging to active product (only used when viewing a 'product')
     */
    const productRelatedItems$ = this.product$.pipe(
      distinctUntilChanged(),
      switchMap(() => {
        return this.productDetailsService.getRelatedItems().pipe(filter((d) => !!d));
      }),
      withdrawOn(contextChanged$),
      map((productRelations) => productRelations?.relations || [])
    );

    this.subscription.add(
      productRelatedItems$.pipe(take(1)).subscribe((relatedItems: ProductRelation[]) => {
        this.eventService.dispatch(new ViewRelatedItemsEvent(relatedItems));
      })
    );

    const relatedProducts$ = productRelatedItems$.pipe(
      map((relatedItems) => relatedItems?.filter((item) => item.targetType !== ProductRelationTargetType.ARTICLE) || []),
      switchMap(
        (relatedProducts): Observable<ProductCarouselItem[]> =>
          zip(
            ...relatedProducts.map((relatedProduct) =>
              this.productService
                .getProduct(relatedProduct.target)
                .pipe(map((product) => ({ product, link: product?.url && prepareUrlForLink(product.url) })))
            )
          )
      ),
      map((relatedProducts) => relatedProducts.filter((relatedProduct) => Boolean(relatedProduct.product))),
      startWith([]),
      withdrawOn(contextChanged$),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.relatedProducts$ = relatedProducts$;
    this.hasRelatedProducts$ = relatedProducts$.pipe(
      map((relatedProducts) => relatedProducts?.length > 0),
      distinctUntilChanged()
    );

    this.relatedArticles$ = productRelatedItems$.pipe(
      map((relatedItems) => relatedItems?.filter((item) => item.targetType === ProductRelationTargetType.ARTICLE) || []),
      map((relatedArticles) => relatedArticles.map((relatedArticle) => relatedArticle.target)),
      map((articleCodes) => articleCodes.filter((code, index) => articleCodes.indexOf(code) === index)),
      switchMap((articleCodes) => (articleCodes?.length ? this.articleService.getArticlesFully(articleCodes) : of([]))),
      map((articles) =>
        this.convertArticlesToArticleCarouselItems(
          articles?.filter((article) => !this.articleService.isArticleDiscontinued(article))
        )
      ),
      startWith([]),
      withdrawOn(contextChanged$),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private convertArticlesToArticleCarouselItems(articles?: Article[]): ArticleCarouselItem[] {
    return articles?.map((article) => ({
      article: article,
    }));
  }

  setQueryParams(queryParams: { ks: boolean | null; a: string | null }) {
    this.router
      .navigate([], {
        queryParams,
        queryParamsHandling: 'merge',
        relativeTo: this.activatedRoute,
      })
      .then((succeeded) => {
        if (succeeded) {
          setTimeout(() => {
            this.windowUtils.scrollIntoView(this.pdpOverview?.nativeElement);
          }, 100);
        }
      });
  }

  setArticleOutOfStock(event: { status: boolean; outOfStockOnWebshop: boolean }): void {
    this.outOfStock = event.status;
    this.cd.detectChanges();
  }

  hasCertCustomization(certs: ProductCertification, type: ProductCertificateGroupCustomizationEnum): boolean {
    const certsWithType = certs?.groups?.flatMap((group) => (group?.customization === type ? group.certificates : [])) ?? [];

    return !!certsWithType?.length;
  }
}
